import React, { useState, useEffect, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  FileText, Github, Linkedin, Mail,
  GraduationCap, CircleCheck, ChevronDown
} from 'lucide-react';
import ResumeModal from './ResumeModal';
import { useOnScreen } from '../utils/useOnScreen';
import dp from '../images/dp.jpg';
import './About.css';

// Resume URL for viewing and downloading
const resumeUrl = 'https://drive.google.com/file/d/17dwsxIp4fptxnEb2Ae54fa7WtuLq5EL8/preview';
const resumeDownloadUrl = 'https://drive.google.com/uc?export=download&id=17dwsxIp4fptxnEb2Ae54fa7WtuLq5EL8';

// Rotating focus line: "Building <focus>". All phrases are stacked in one grid cell,
// so the line never changes width or height while it rotates.
const focusAreas = ['multi-agent AI systems', 'full-stack products', 'AI you can trust'];
const ROTATE_MS = 2800;

const socials = [
  { label: 'GitHub', href: 'https://github.com/phanicharan04', icon: Github },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/in/phanicharan04', icon: Linkedin },
  { label: 'Email', href: 'mailto:phanicharan116@gmail.com', icon: Mail }
];

const EASE = [0.22, 1, 0.36, 1];

const About = () => {
  const [modalOpen, setModalOpen] = useState(false);
  // Set once the visitor goes for the resume button, so the PDF starts loading just before the click
  const [resumeIntent, setResumeIntent] = useState(false);
  const [focusIndex, setFocusIndex] = useState(0);
  const reduceMotion = useReducedMotion();
  const sectionRef = useRef(null);
  // Endless animations and the phrase rotation only run while the hero can actually be seen
  const onScreen = useOnScreen(sectionRef);

  useEffect(() => {
    if (reduceMotion || !onScreen) return undefined;
    const timer = setInterval(() => setFocusIndex((i) => (i + 1) % focusAreas.length), ROTATE_MS);
    return () => clearInterval(timer);
  }, [reduceMotion, onScreen]);

  const scrollTo = (selector) => (e) => {
    e.preventDefault();
    document.querySelector(selector)?.scrollIntoView({ behavior: 'smooth' });
  };

  // Staggered entrance on load (hero is above the fold, so no scroll trigger).
  const rise = (delay = 0) =>
    reduceMotion
      ? {}
      : {
          initial: { opacity: 0, y: 24 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.6, delay, ease: EASE }
        };

  return (
    <section ref={sectionRef} className={`about-section ${onScreen ? '' : 'is-paused'}`} id="about">
      <div className="hero-container">
        <div className="hero-grid">
          {/* ---------- Text ---------- */}
          <div className="hero-text">
            <motion.div className="hero-chip" {...rise(0)}>
              <span className="hero-chip-dot" aria-hidden="true" />
              AI Engineer at Azentio Software
            </motion.div>

            <motion.h1 className="hero-name" aria-label="Venkata Phani Charan Udumula" {...rise(0.08)}>
              <span className="hero-name-line" aria-hidden="true">
                Venkata <span className="hero-name-accent">Phani</span>
              </span>
              <span className="hero-name-line" aria-hidden="true">
                <span className="hero-name-accent">Charan</span> Udumula
              </span>
            </motion.h1>

            <motion.p className="hero-focus" {...rise(0.16)}>
              <span>Building&nbsp;</span>
              <span className="hero-rotor" aria-hidden="true">
                {focusAreas.map((area, i) => (
                  <motion.span
                    key={area}
                    animate={{
                      opacity: i === focusIndex ? 1 : 0,
                      y: i === focusIndex ? 0 : i === (focusIndex + 1) % focusAreas.length ? 14 : -14
                    }}
                    transition={{ duration: reduceMotion ? 0 : 0.4, ease: EASE }}
                  >
                    {area}
                  </motion.span>
                ))}
              </span>
              <span className="hero-sr">{focusAreas.join(', ')}</span>
            </motion.p>

            <motion.div className="hero-pitch" {...rise(0.24)}>
              <p className="hero-pitch-lead">
                Building a successful product is a challenge. Plenty of engineers can write prompts, train models, and build demos.
              </p>
              <p className="hero-pitch-main">
                I build and handle what makes AI hold up in production: <mark>evaluation</mark>,{' '}
                <mark>cost optimization</mark>, <mark>data cleaning</mark>, and{' '}
                <mark>monitoring</mark>, from first concept to deployment and beyond.
              </p>
            </motion.div>

            <motion.div className="hero-actions" {...rise(0.32)}>
              <a
                href="#contact"
                className="hero-btn hero-btn-primary"
                onClick={scrollTo('#contact')}
              >
                Let&apos;s connect
              </a>

              <button
                type="button"
                className="hero-btn hero-btn-ghost"
                onClick={() => setModalOpen(true)}
                onPointerEnter={() => setResumeIntent(true)}
                onFocus={() => setResumeIntent(true)}
                onTouchStart={() => setResumeIntent(true)}
              >
                <FileText size={18} />
                View resume
              </button>

              <ul className="hero-socials" aria-label="Social links">
                {socials.map(({ label, href, icon: Icon }) => (
                  <li key={label}>
                    <a
                      href={href}
                      className="hero-social"
                      aria-label={label}
                      title={label}
                      {...(href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    >
                      <Icon size={18} />
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>

          </div>

          {/* ---------- Portrait ---------- */}
          <motion.div
            className="hero-visual"
            {...(reduceMotion
              ? {}
              : {
                  initial: { opacity: 0, scale: 0.94 },
                  animate: { opacity: 1, scale: 1 },
                  transition: { duration: 0.8, delay: 0.2, ease: EASE }
                })}
          >
            <div className="hero-glow" aria-hidden="true" />
            <div className="hero-ring hero-ring-1" aria-hidden="true" />
            <div className="hero-ring hero-ring-2" aria-hidden="true" />

            <div className="hero-arch">
              <img src={dp} alt="U V Phani Charan" />
            </div>

            <div className="hero-badge hero-badge-1">
              <div className="hero-badge-inner">
                <GraduationCap size={15} />
                Class of 2025
              </div>
            </div>

            <div className="hero-badge hero-badge-2">
              <div className="hero-badge-inner hero-badge-mono" style={{ animationDelay: '1.4s' }}>
                <CircleCheck size={15} />
                BUILD SUCCESS
              </div>
            </div>

            <div className="hero-badge hero-badge-4">
              <div className="hero-badge-inner hero-badge-mono" style={{ animationDelay: '3.2s' }}>
                <span className="hero-badge-dot" aria-hidden="true" />
                GET /me
                <span className="hero-badge-status">200 OK</span>
              </div>
            </div>

            <div className="hero-badge hero-badge-3">
              <div className="hero-badge-inner hero-badge-mono" style={{ animationDelay: '2.6s' }}>
                found <span className="hero-badge-status">0</span> vulnerabilities
              </div>
            </div>
          </motion.div>
        </div>

      </div>

      <a href="#experience" className="hero-scroll" onClick={scrollTo('#experience')}>
        <span>Scroll</span>
        <ChevronDown size={16} />
      </a>

      <ResumeModal
        open={modalOpen}
        preload={resumeIntent}
        onClose={() => setModalOpen(false)}
        previewUrl={resumeUrl}
        downloadUrl={resumeDownloadUrl}
      />
    </section>
  );
};

export default About;
